import { recipientCopy, recipientCopyKey, withPreservedRecipients } from './recipient-preservation';
import { localDataChanged } from './local-data-events';
import { normalizeRecoverySource, type RecoverySource } from './recovery-copy';
import { packingStateSchema, savedBagProfileSchema, validateStateReferences, type PackingState, type SavedBagProfile } from '../domain/schema';
import { normalizeReadinessState } from '../domain/readiness';
import { assertTripCapacity, mergeTrip, sameValue, TripConflictError } from './trip-merge';
import type { Rights } from '../domain/entitlement';
import { currentRights } from '../domain/entitlement-snapshot';

function assertActiveCapacity(current: unknown[], incoming: PackingState[], rights: Rights) {
  const states = new Map<string, PackingState>();
  for (const raw of current) {
    const parsed = packingStateSchema.safeParse(raw);
    if (!parsed.success) throw new StorageError('읽지 못한 원본이 있어 새 여행 저장을 멈췄어요. 내 여행에서 원본을 먼저 확인해주세요.');
    states.set(parsed.data.trip.id, parsed.data);
  }
  const before = [...states.values()].filter((state) => state.trip.status !== 'archived').length;
  for (const state of incoming) states.set(state.trip.id, state);
  const after = [...states.values()].filter((state) => state.trip.status !== 'archived').length;
  if (after > Math.max(rights.maxTrips, before)) throw new StorageError(`현재 권리에서는 진행 중인 여행을 ${rights.maxTrips}개까지 둘 수 있어요. 여행 하나를 보관하면 다시 만들거나 꺼낼 수 있어요.`);
}

const DB_NAME = 'carryonpan';
export const DB_VERSION = 3;
const PREFERENCES_STORE = 'preferences';
const STORE = 'trips';
const BAG_PROFILE_STORE = 'bagProfiles';

export class StorageError extends Error {
  constructor(message: string, readonly causeValue?: unknown) {
    super(message);
    this.name = 'StorageError';
    (globalThis as {__carryonpanStorageError?:string}).__carryonpanStorageError = 'StorageError';
  }
}

export interface CorruptTripRecord {
  key: string;
  raw: unknown;
  reason: string;
}

export interface TripInspection {
  trips: PackingState[];
  corrupt: CorruptTripRecord[];
}

export class CorruptTripsError extends StorageError {
  constructor(readonly records: CorruptTripRecord[]) {
    super('읽지 못한 여행 기록이 있어 빈 목록으로 바꾸지 않았어요.');
    this.name = 'CorruptTripsError';
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PREFERENCES_STORE)) db.createObjectStore(PREFERENCES_STORE, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'trip.id' });
      if (!db.objectStoreNames.contains(BAG_PROFILE_STORE)) db.createObjectStore(BAG_PROFILE_STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => { request.result.onversionchange=()=>request.result.close(); resolve(request.result); };
    request.onerror = () => reject(new StorageError('기기 저장소를 열지 못했어요.', request.error));
  });
}

export async function saveBagProfile(profile: SavedBagProfile): Promise<void> {
  const parsed = savedBagProfileSchema.parse(profile);
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(BAG_PROFILE_STORE, 'readwrite');
    transaction.objectStore(BAG_PROFILE_STORE).put(parsed);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(new StorageError('가방 프로필을 저장하지 못했어요.', transaction.error));
    transaction.onabort = () => reject(new StorageError('가방 프로필 저장이 중단됐어요.', transaction.error));
  });
  db.close();
  localDataChanged();
}

export async function listBagProfiles(): Promise<SavedBagProfile[]> {
  const db = await openDb();
  const values = await new Promise<unknown[]>((resolve, reject) => {
    const request = db.transaction(BAG_PROFILE_STORE, 'readonly').objectStore(BAG_PROFILE_STORE).getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new StorageError('가방 프로필을 읽지 못했어요.', request.error));
  });
  db.close();
  return values.flatMap((value) => {
    const parsed = savedBagProfileSchema.safeParse(value);
    return parsed.success ? [parsed.data] : [];
  }).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function deleteBagProfile(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(BAG_PROFILE_STORE, 'readwrite');
    transaction.objectStore(BAG_PROFILE_STORE).delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(new StorageError('가방 프로필을 지우지 못했어요.', transaction.error));
  });
  db.close();
  localDataChanged();
}

export async function saveTrip(state: PackingState, expected: PackingState | null, rights: Rights = currentRights()): Promise<PackingState> {
  const parsed = packingStateSchema.parse(normalizeReadinessState(state));
  const references = validateStateReferences(parsed);
  if (references.length) throw new StorageError(`저장할 자료의 연결이 올바르지 않아요: ${references[0]}`);
  const db = await openDb();
  try { await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([STORE, PREFERENCES_STORE], 'readwrite');
    let failure: unknown;
    const store = transaction.objectStore(STORE);
    const copies = transaction.objectStore(PREFERENCES_STORE);
    let preserved: unknown;
    const copyRequest = copies.get(recipientCopyKey(parsed.trip.id));
    copyRequest.onsuccess = () => { preserved = copyRequest.result?.value; };
    const commit = (value: PackingState) => { store.put(value); copies.put(recipientCopy(value)); };
    const current = store.get(parsed.trip.id);
    current.onsuccess = () => {
      try {
        if (expected === null) {
          if (current.result !== undefined) throw new TripConflictError();
          assertTripCapacity(parsed, undefined, rights);
          const all = store.getAll();
          all.onsuccess = () => { try { assertActiveCapacity(all.result, [parsed], rights); commit(parsed); } catch (error) { failure = error; transaction.abort(); } };
        } else {
          if (current.result === undefined) throw new TripConflictError();
          const remote = withPreservedRecipients(packingStateSchema.parse(normalizeReadinessState(packingStateSchema.parse(current.result))), preserved);
          const merged = mergeTrip(packingStateSchema.parse(normalizeReadinessState(expected)), parsed, remote, rights);
          if (remote.trip.status === 'archived' && merged.trip.status !== 'archived') {
            const all = store.getAll();
            all.onsuccess = () => { try { assertActiveCapacity(all.result, [merged], rights); commit(merged); } catch (error) { failure = error; transaction.abort(); } };
          } else commit(merged);
        }
      } catch (error) { failure = error; transaction.abort(); }
    };
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(failure ?? new StorageError('자동 저장하지 못했어요.', transaction.error));
    transaction.onabort = () => reject(failure ?? new StorageError('자동 저장이 중단됐어요.', transaction.error));
  }); } finally { db.close(); }
  const saved = await loadTrip(parsed.trip.id);
  if (!saved) throw new TripConflictError();
  localDataChanged();
  return saved;
}

export async function loadTrip(id: string): Promise<PackingState | null> {
  const db = await openDb();
  const transaction = db.transaction([STORE, PREFERENCES_STORE], 'readonly');
  const [value, copy] = await Promise.all([
    requestResult(transaction.objectStore(STORE).get(id), '저장된 여행을 읽지 못했어요.'),
    requestResult(transaction.objectStore(PREFERENCES_STORE).get(recipientCopyKey(id)), '물건 주인 보관본을 읽지 못했어요.'),
  ]);
  db.close();
  if (!value) return null;
  const parsed = packingStateSchema.safeParse(value);
  if (!parsed.success) throw new CorruptTripsError([{ key: id, raw: value, reason: parsed.error.issues[0]?.message ?? '여행 형식이 올바르지 않아요.' }]);
  const references = validateStateReferences(parsed.data);
  const referenceError = references[0];
  if (referenceError) throw new CorruptTripsError([{ key: id, raw: value, reason: referenceError }]);
  return withPreservedRecipients(packingStateSchema.parse(normalizeReadinessState(parsed.data)), copy?.value);
}

export function classifyTripRecords(values: unknown[], keys: IDBValidKey[]): TripInspection {
  const trips: PackingState[] = [];
  const corrupt: CorruptTripRecord[] = [];
  values.forEach((value, index) => {
    const key = String(keys[index] ?? `unknown-${index + 1}`);
    const parsed = packingStateSchema.safeParse(value);
    if (!parsed.success) {
      corrupt.push({ key, raw: value, reason: parsed.error.issues[0]?.message ?? '여행 형식이 올바르지 않아요.' });
      return;
    }
    const references = validateStateReferences(parsed.data);
    const referenceError = references[0];
    if (referenceError) {
      corrupt.push({ key, raw: value, reason: referenceError });
      return;
    }
    trips.push(packingStateSchema.parse(normalizeReadinessState(parsed.data)));
  });
  trips.sort((a, b) => b.trip.updatedAt.localeCompare(a.trip.updatedAt));
  return { trips, corrupt };
}

export async function inspectTrips(): Promise<TripInspection> {
  const db = await openDb();
  const transaction = db.transaction([STORE, PREFERENCES_STORE], 'readonly');
  const store = transaction.objectStore(STORE);
  const [values, keys, copies] = await Promise.all([
    requestResult(store.getAll(), '여행 목록을 읽지 못했어요.'),
    requestResult(store.getAllKeys(), '여행 식별자를 읽지 못했어요.'),
    requestResult(transaction.objectStore(PREFERENCES_STORE).getAll(), '물건 주인 보관본을 읽지 못했어요.'),
  ]);
  db.close();
  const inspection = classifyTripRecords(values, keys);
  return { ...inspection, trips: inspection.trips.map(state => withPreservedRecipients(state, copies.find(row => row.key === recipientCopyKey(state.trip.id))?.value)) };
}

function requestResult<T>(request: IDBRequest<T>, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new StorageError(message, request.error));
  });
}

export async function listTrips(): Promise<PackingState[]> {
  const inspection = await inspectTrips();
  if (inspection.corrupt.length > 0) throw new CorruptTripsError(inspection.corrupt);
  return inspection.trips;
}

export function exportCorruptTrips(records: CorruptTripRecord[], now = new Date()): string {
  return JSON.stringify({ schemaVersion: 1, product: 'carryonpan', kind: 'raw-trip-recovery', exportedAt: now.toISOString(), records }, (key, value) => key === 'remoteShare' ? undefined : value, 2);
}

export async function deleteCorruptTrips(expected: CorruptTripRecord[]): Promise<void> {
  const targets = expected;
  if (targets.length === 0) return;
  const db = await openDb();
  try { await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite');
    let conflict = false;
    const store = transaction.objectStore(STORE);
    for (const record of targets) {
      const current = store.get(record.key);
      current.onsuccess = () => {
        if (!sameValue(current.result, record.raw) || !classifyTripRecords([current.result], [record.key]).corrupt.length) { conflict = true; transaction.abort(); }
        else store.delete(record.key);
      };
    }
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(conflict ? new TripConflictError() : new StorageError('읽지 못한 여행을 초기화하지 못했어요.', transaction.error));
    transaction.onabort = () => reject(conflict ? new TripConflictError() : new StorageError('읽지 못한 여행 초기화가 중단됐어요.', transaction.error));
  }); } finally { db.close(); }
  localDataChanged();
}

export async function importTrips(states: PackingState[], expected: readonly PackingState[], rights: Rights = currentRights()): Promise<void> {
  if (new Set(states.map((state) => state.trip.id)).size !== states.length) throw new StorageError('파일에 같은 여행 ID가 중복되어 있어요. 원본은 바뀌지 않았습니다.');
  const parsed = states.map((state) => {
    const value = packingStateSchema.parse(state);
    const references = validateStateReferences(value);
    if (references.length) throw new StorageError(`가져올 자료의 연결이 올바르지 않아요: ${references[0]}`);
    return packingStateSchema.parse(normalizeReadinessState(value));
  });
  const db = await openDb();
  try { await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([STORE, PREFERENCES_STORE], 'readwrite');
    let conflict = false;
    const store = transaction.objectStore(STORE);
    const all = store.getAll();
    all.onsuccess = () => { try { assertActiveCapacity(all.result, parsed, rights); } catch { conflict = true; transaction.abort(); } };
    for (const state of parsed) {
      let preserved: unknown;
      const copyRequest = transaction.objectStore(PREFERENCES_STORE).get(recipientCopyKey(state.trip.id));
      copyRequest.onsuccess = () => { preserved = copyRequest.result?.value; };
      const request = store.get(state.trip.id);
      request.onsuccess = () => {
        const base = expected.find((entry) => entry.trip.id === state.trip.id);
        let current: unknown;
        try { current = request.result === undefined ? undefined : withPreservedRecipients(packingStateSchema.parse(normalizeReadinessState(packingStateSchema.parse(request.result))), preserved); }
        catch { conflict = true; transaction.abort(); return; }
        if (!sameValue(current, base)) { conflict = true; transaction.abort(); }
        else { store.put(state); transaction.objectStore(PREFERENCES_STORE).put(recipientCopy(state)); }
      };
    }
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(conflict ? new TripConflictError() : new StorageError('가져온 여행을 저장하지 못했어요.', transaction.error));
    transaction.onabort = () => reject(conflict ? new TripConflictError() : new StorageError('여행 가져오기가 중단됐어요.', transaction.error));
  }); } finally { db.close(); }
  localDataChanged();
}

export async function deleteAllLocalData(): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(new StorageError('기기 자료를 지우지 못했어요.', request.error));
    request.onblocked = () => reject(new StorageError('열려 있는 다른 캐리온판 창을 닫아주세요.'));
  });
  for (const key of Object.keys(localStorage)) if (key.startsWith('carryonpan.') || key === 'juice.muted') localStorage.removeItem(key);
}

export function exportJson(states: PackingState[]): string {
  return JSON.stringify({
    schemaVersion: 1,
    product: 'carryonpan',
    writtenBy: __BUILD_VERSION__,
    exportedAt: new Date().toISOString(),
    trips: states.map((state) => {
      const normalized = normalizeReadinessState(state);
      return { ...normalized, trip: { ...normalized.trip, remoteShare: undefined } };
    }),
  }, null, 2);
}

export function previewImport(text: string): { ok: true; trips: PackingState[]; summary: string } | { ok: false; message: string } {
  try {
    const raw = JSON.parse(text) as { schemaVersion?: unknown; product?: unknown; trips?: unknown };
    if (raw.schemaVersion !== 1 || raw.product !== 'carryonpan' || !Array.isArray(raw.trips)) return { ok: false, message: '캐리온판에서 만든 여행 백업 파일이 아니에요.' };
    const trips: PackingState[] = [];
    for (const [index, value] of raw.trips.entries()) {
      const parsed = packingStateSchema.safeParse(value);
      if (!parsed.success) return { ok: false, message: `${index + 1}번째 여행의 ${parsed.error.issues[0]?.path.join('.') || '자료'}를 확인해주세요.` };
      const references = validateStateReferences(parsed.data);
      if (references.length) return { ok: false, message: `${index + 1}번째 여행: ${references[0]}` };
      trips.push(packingStateSchema.parse(normalizeReadinessState(parsed.data)));
    }
    return { ok: true, trips, summary: `여행 ${trips.length}개 · 가방 ${trips.reduce((sum, state) => sum + state.bags.length, 0)}개 · 물건 ${trips.reduce((sum, state) => sum + state.items.length, 0)}개 · 경비 ${trips.reduce((sum, state) => sum + (state.trip.expenses?.length ?? 0), 0)}건` };
  } catch {
    return { ok: false, message: '여행 백업 파일을 읽을 수 없어요. 원본 파일은 바뀌지 않았어요.' };
  }
}

/** Both stores are read from one transaction; an unreadable original blocks copying. */
export async function readRecoverySource(): Promise<RecoverySource> {
  const db = await openDb();
  try {
    const tx = db.transaction([STORE, BAG_PROFILE_STORE, PREFERENCES_STORE], 'readonly');
    const [trips, bagProfiles, copies] = await Promise.all([
      requestResult(tx.objectStore(STORE).getAll(), '여행 원본을 읽지 못했어요.'),
      requestResult(tx.objectStore(BAG_PROFILE_STORE).getAll(), '내 가방 원본을 읽지 못했어요.'),
      requestResult(tx.objectStore(PREFERENCES_STORE).getAll(), '물건 주인 보관본을 읽지 못했어요.'),
    ]);
    const normalized = normalizeRecoverySource({ trips, bagProfiles });
    return { ...normalized, trips: normalized.trips.map(state => withPreservedRecipients(state, copies.find(row => row.key === recipientCopyKey(state.trip.id))?.value)) };
  } finally { db.close(); }
}

/** Explicit selected restore only, with the preview's observed values as CAS bases. */
export async function restoreRecoverySource(incoming: RecoverySource, expected: RecoverySource): Promise<void> {
  const parsed = normalizeRecoverySource(incoming);
  const db = await openDb();
  try { await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE, BAG_PROFILE_STORE, PREFERENCES_STORE], 'readwrite');
    let failure: unknown;
    const abort = (error: unknown) => { failure = error; tx.abort(); };
    const trips = tx.objectStore(STORE);
    const all = trips.getAll();
    all.onsuccess = () => { try { assertActiveCapacity(all.result, parsed.trips, currentRights()); } catch (error) { abort(error); } };
    for (const trip of parsed.trips) {
      let preserved: unknown;
      const copyRequest = tx.objectStore(PREFERENCES_STORE).get(recipientCopyKey(trip.trip.id));
      copyRequest.onsuccess = () => { preserved = copyRequest.result?.value; };
      const request = trips.get(trip.trip.id);
      request.onsuccess = () => {
        try {
          const current = request.result === undefined ? undefined : withPreservedRecipients(packingStateSchema.parse(normalizeReadinessState(packingStateSchema.parse(request.result))), preserved);
          const base = expected.trips.find((value) => value.trip.id === trip.trip.id);
          const safeCurrent = current ? normalizeRecoverySource({ trips: [current], bagProfiles: [] }).trips[0] : undefined;
          if (!sameValue(safeCurrent, base)) throw new TripConflictError();
          trips.put({ ...trip, trip: { ...trip.trip, remoteShare: current?.trip.remoteShare } });
          tx.objectStore(PREFERENCES_STORE).put(recipientCopy(trip));
        } catch (error) { abort(error); }
      };
    }
    const bags = tx.objectStore(BAG_PROFILE_STORE);
    for (const profile of parsed.bagProfiles) {
      const request = bags.get(profile.id);
      request.onsuccess = () => {
        try {
          if (!sameValue(request.result, expected.bagProfiles.find((base) => base.id === profile.id))) throw new TripConflictError();
          bags.put(profile);
        } catch (error) { abort(error); }
      };
    }
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(failure ?? new StorageError('복원하지 못했어요. 현재 원본과 사본은 그대로예요.'));
  }); } finally { db.close(); }
  const readback = await readRecoverySource();
  if (parsed.trips.some((trip) => !sameValue(trip, readback.trips.find((value) => value.trip.id === trip.trip.id))) || parsed.bagProfiles.some((bag) => !sameValue(bag, readback.bagProfiles.find((value) => value.id === bag.id)))) throw new TripConflictError();
  localDataChanged();
}

/** Preferences stay on this device, outside travel sharing and event payloads. */
export async function readPreference<T>(key: string, fallback: T): Promise<T> {
  const db = await openDb();
  try { return await new Promise<T>((resolve, reject) => {
    const request = db.transaction(PREFERENCES_STORE, 'readonly').objectStore(PREFERENCES_STORE).get(key);
    request.onsuccess = () => resolve(request.result === undefined ? fallback : request.result.value as T);
    request.onerror = () => reject(new StorageError('기기 설정을 읽지 못했어요.', request.error));
  }); } finally { db.close(); }
}
export async function writePreference<T>(key: string, value: T): Promise<void> {
  const db = await openDb();
  try { await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(PREFERENCES_STORE, 'readwrite');
    transaction.objectStore(PREFERENCES_STORE).put({ key, value });
    transaction.oncomplete = () => resolve();
    transaction.onerror = transaction.onabort = () => reject(new StorageError('기기 설정을 저장하지 못했어요.', transaction.error));
  }); localDataChanged(); } finally { db.close(); }
}

/** Compare the exact last observed source before deleting just one trip. */
export async function deleteTrip(id: string, expected: PackingState): Promise<void> {
  const db = await openDb();
  try { await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE, PREFERENCES_STORE], 'readwrite'), store = tx.objectStore(STORE);
    let failure: unknown, preserved: unknown;
    const copy = tx.objectStore(PREFERENCES_STORE).get(recipientCopyKey(id));
    copy.onsuccess = () => { preserved = copy.result?.value; };
    const request = store.get(id);
    request.onsuccess = () => { try {
      if (request.result === undefined) throw new TripConflictError();
      const current = withPreservedRecipients(packingStateSchema.parse(normalizeReadinessState(request.result)), preserved);
      if (!sameValue(current, packingStateSchema.parse(normalizeReadinessState(expected)))) throw new TripConflictError();
      store.delete(id); tx.objectStore(PREFERENCES_STORE).delete(recipientCopyKey(id));
    } catch (error) { failure = error; tx.abort(); } };
    tx.oncomplete = () => resolve();
    tx.onerror = tx.onabort = () => reject(failure ?? new StorageError('여행을 삭제하지 못했어요.', tx.error));
  }); } finally { db.close(); }
  localDataChanged();
}
